export const JSON_CONTENT_TYPE_UTF8 = "application/json;charset=UTF-8";

const hasTextDecoder = typeof TextDecoder !== "undefined";

const latin1FromBytes = (bytes) => {
  // Chunked so a long body cannot blow the argument limit of String.fromCharCode.
  let out = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + 0x8000)
    );
  }
  return out;
};

const utf8FromBytes = (bytes, fatal) => {
  if (hasTextDecoder) {
    return new TextDecoder("utf-8", { fatal }).decode(bytes);
  }

  const decoded = decodeURIComponent(escape(latin1FromBytes(bytes)));
  return decoded;
};

const toBytes = (body) => {
  if (body instanceof Uint8Array) return body;
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(body)) {
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
  }
  return new Uint8Array(body);
};

/**
 * Decode a raw response body as UTF-8, ignoring whatever charset the server
 * claimed. Strings pass through untouched so callers can stay agnostic about
 * the configured responseType.
 */
export function decodeUtf8(body) {
  if (body == null) return "";
  if (typeof body === "string") return body;

  const bytes = toBytes(body);
  if (bytes.length === 0) return "";

  try {
    return utf8FromBytes(bytes, false);
  } catch (e) {
    return latin1FromBytes(bytes);
  }
}

/**
 * Mirror axios' default transformResponse: hand back parsed JSON when the body
 * is JSON, and the raw text when it is not.
 */
export function parseResponseBody(text) {
  if (typeof text !== "string" || text === "") return text;
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

/**
 * Code points windows-1252 puts in 0x80-0x9F, in byte order. Browsers apply the
 * windows-1252 table for the label "ISO-8859-1" (the WHATWG Encoding Standard
 * makes the former an alias of the latter), so a UTF-8 byte such as 0x87
 * surfaces as "‡" (U+2021) rather than U+0087 -- which is why "Ç" (C3 87)
 * arrives as "Ã‡" and not "Ã".
 */
const CP1252_C1_CODE_POINTS = [
  0x20ac, 0x0081, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021,
  0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008d, 0x017d, 0x008f,
  0x0090, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x009d, 0x017e, 0x0178
];

const CP1252_TO_BYTE = CP1252_C1_CODE_POINTS.reduce((map, codePoint, index) => {
  map[codePoint] = 0x80 + index;
  return map;
}, {});

export function repairMojibake(value) {
  if (typeof value !== "string" || value === "") return value;

  const bytes = new Uint8Array(value.length);
  let hasHighByte = false;

  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    let byte;

    if (code <= 0xff) {
      byte = code;
    } else {
      byte = CP1252_TO_BYTE[code];
      if (byte === undefined) return value; // real text, not a mis-decode
    }

    if (byte > 0x7f) hasHighByte = true;
    bytes[i] = byte;
  }

  if (!hasHighByte) return value; // pure ASCII: nothing to repair

  try {
    return utf8FromBytes(bytes, true);
  } catch (e) {
    return value; // genuine single-byte text, leave it be
  }
}

const HIGH_BYTE_ESCAPE = /%[89A-Fa-f][0-9A-Fa-f]/;

export function decodeLegacyPercentEncoding(value) {
  if (typeof value !== "string" || value.indexOf("%") === -1) return value;
  if (!HIGH_BYTE_ESCAPE.test(value)) return value;

  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

export function normalizeApiText(value) {
  if (typeof value !== "string") return value;
  return repairMojibake(decodeLegacyPercentEncoding(value));
}
